export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "msclkid"];
// Everything kept for 90 days and used once: the ad click IDs plus Meta's click ID
// and OpenAI's click reference.
export const STORED_ID_KEYS = [...CLICK_ID_KEYS, "fbclid", "oppref"];
export const CLICK_ID_TTL_DAYS = 90;
const USED_FBC_KEY = "fbc_used";

const PARTNER_KEYS = ["ps_xid", "sscid", "irclickid"];
const AD_TOUCH_KEYS = ["utm_source", "utm_medium", "utm_campaign", ...CLICK_ID_KEYS, "oppref"];
const TOUCH_KEYS = [...AD_TOUCH_KEYS, ...PARTNER_KEYS];

const hasAny = (search, keys) => {
  try {
    const params = new URLSearchParams(search);
    return keys.some(key => params.get(key));
  } catch (_) {
    return false;
  }
};

/**
 * Whether a URL starts a new ad touch, which replaces the saved one (last click
 * wins). A button name alone (utm_content) or a plain visit does not.
 */
export const startsNewTouch = search => hasAny(search, TOUCH_KEYS);

const expiresKey = key => `${key}_expires`;

const getLocalStorage = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
    if (typeof localStorage !== "undefined") {
      return localStorage;
    }
  } catch (_) {
    return null;
  }
  return null;
};

const safeGetItem = key => {
  try {
    const storage = getLocalStorage();
    return typeof storage?.getItem === "function" ? storage.getItem(key) : null;
  } catch (_) {
    return null;
  }
};

const safeSetItem = (key, value) => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.setItem === "function") {
      storage.setItem(key, value);
    }
  } catch (_) {
    return;
  }
};

const safeRemoveItem = key => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.removeItem === "function") {
      storage.removeItem(key);
    }
  } catch (_) {
    return;
  }
};

const getCookieDomain = () => {
  if (typeof window === "undefined") return undefined;
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return undefined;

  // app.staging-spocket.co / alpha.staging-spocket.co → .staging-spocket.co
  if (hostname.includes("staging-spocket.co")) return ".staging-spocket.co";
  // app.spocket.co → .spocket.co
  if (hostname.endsWith("spocket.co")) return ".spocket.co";

  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }
  return undefined;
};

const setCookie = (name, value, days) => {
  if (typeof document === "undefined") return;
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const domain = getCookieDomain();
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
      value
    )}; expires=${expires}; path=/; SameSite=Lax`;
    if (domain) cookie += `; domain=${domain}`;
    const isHttps = typeof window !== "undefined" && window.location?.protocol === "https:";
    if (isHttps) cookie += "; Secure";
    document.cookie = cookie;
  } catch (_) {
    return;
  }
};

const getCookie = name => {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch (_) {
    return null;
  }
};

const persistClickId = (key, value, { overwrite = false } = {}) => {
  if (!overwrite) {
    const existing = getPersistedClickId(key);
    if (existing) return;
  }

  const expiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
  safeSetItem(key, value);
  safeSetItem(expiresKey(key), String(expiresAt));
  setCookie(key, value, CLICK_ID_TTL_DAYS);
};

const clearClickId = key => {
  safeRemoveItem(key);
  safeRemoveItem(expiresKey(key));
  setCookie(key, "", -1);
};

export const getPersistedClickId = key => {
  const expiresRaw = safeGetItem(expiresKey(key));
  const expiresAt = expiresRaw !== null ? Number(expiresRaw) : NaN;
  const hasValidExpiry = Number.isFinite(expiresAt);

  if (hasValidExpiry) {
    if (Date.now() > expiresAt) {
      safeRemoveItem(key);
      safeRemoveItem(expiresKey(key));
    } else {
      const fromLs = safeGetItem(key);
      if (fromLs) return fromLs;
    }
  } else {
    // Legacy / missing expiry: attach a fresh TTL so the value can still expire
    const fromLs = safeGetItem(key);
    if (fromLs) {
      const newExpiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
      safeSetItem(expiresKey(key), String(newExpiresAt));
      setCookie(key, fromLs, CLICK_ID_TTL_DAYS);
      return fromLs;
    }
    if (expiresRaw !== null) safeRemoveItem(expiresKey(key));
  }

  return getCookie(key);
};

export const getAllPersistedClickIds = () => ({
  gclid: getPersistedClickId("gclid"),
  gbraid: getPersistedClickId("gbraid"),
  wbraid: getPersistedClickId("wbraid"),
  msclkid: getPersistedClickId("msclkid")
});

// The Meta and OpenAI IDs, kept apart from the ad click IDs above (which also decide
// the google / bing fallback source).
export const getPersistedClickRefs = () => ({
  fbclid: getPersistedClickId("fbclid"),
  oppref: getPersistedClickId("oppref")
});

// Meta's browser ID (_fbp) and click ID (_fbc) cookies. Without a _fbc cookie it is
// built from a stored fbclid in Meta's format, fb.1.<click time in ms>.<fbclid>.
export const readFbCookies = () => {
  const result = {};
  const fbp = getCookie("_fbp");
  if (fbp) result.fbp = fbp;

  // The _fbc cookie outlives the click ID storage, so one already sent is skipped.
  let fbc = getCookie("_fbc");
  if (fbc && fbc === safeGetItem(USED_FBC_KEY)) fbc = null;
  const fbclid = getPersistedClickId("fbclid");
  if (!fbc && fbclid) {
    const expiresAt = Number(safeGetItem(expiresKey("fbclid")));
    const clickedAt =
      Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt - CLICK_ID_TTL_DAYS * 864e5 : Date.now();
    fbc = `fb.1.${clickedAt}.${fbclid}`;
  }
  if (fbc) result.fbc = fbc;
  return result;
};

// Called where a new ad touch replaces the saved spocket_utm cookie, so the stored
// click IDs always belong to the same touch as the cookie (last click wins): a
// Bing click after a Google click doesn't send both, and a UTM-only campaign
// visit drops an older click ID instead of keeping it next to the new campaign.
// A partner link alone (ps_xid, sscid, irclickid) keeps them: the partner and the
// ad click are reported together.
export const replaceStoredClickIds = (
  search = typeof window !== "undefined" ? window.location.search : ""
) => {
  if (hasAny(search, AD_TOUCH_KEYS)) STORED_ID_KEYS.forEach(clearClickId);
  return captureClickIdsFromSearch(search);
};

// Each click ID is used once: after signup has saved it (the app's POST /stores/utm
// succeeded, or answered that the store is already attributed), it is cleared, and
// taken out of the saved spocket_utm touch, so a later signup in this browser can't
// report the same click. The touch's utm_* values are kept.
export const clearAllClickIds = () => {
  try {
    STORED_ID_KEYS.forEach(clearClickId);
    // Meta's pixel owns the _fbc cookie and keeps it for 90 days: remember the
    // value that was sent instead of deleting it, so the browser pixel still has it.
    const sentFbc = getCookie("_fbc");
    if (sentFbc) safeSetItem(USED_FBC_KEY, sentFbc);
    const saved = getCookie("spocket_utm");
    if (!saved) return;

    const query = saved.startsWith("?") ? saved.slice(1) : saved;
    const kept = query.split("&").filter(pair => pair && !STORED_ID_KEYS.includes(pair.split("=")[0]));
    if (kept.length) {
      setCookie("spocket_utm", `?${kept.join("&")}`, 30);
    } else {
      setCookie("spocket_utm", "", -1);
    }
  } catch (_) {
    return;
  }
};

// Runs on every page. It only fills an empty store: replacing click IDs here, on
// a page that keeps the spocket_utm cookie, would pair a new click with an older
// touch's UTMs.
export const captureClickIdsFromSearch = (
  search = typeof window !== "undefined" ? window.location.search : ""
) => {
  const params =
    typeof search === "string" ? new URLSearchParams(search.startsWith("?") ? search : `?${search}`) : search;

  if (STORED_ID_KEYS.some(key => getPersistedClickId(key))) return {};

  const captured = {};
  STORED_ID_KEYS.filter(key => params.get(key)).forEach(key => {
    captured[key] = params.get(key);
    persistClickId(key, captured[key]);
  });
  return captured;
};

/** Append persisted click IDs to a URL when missing (marketing → app domain hop). */
export const appendClickIdsToUrl = url => {
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://app.spocket.co";
    const parsed = new URL(url, origin);
    const clickIds = getAllPersistedClickIds();
    CLICK_ID_KEYS.forEach(key => {
      const value = clickIds[key];
      if (value && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, value);
      }
    });
    return parsed.toString();
  } catch (_) {
    return url;
  }
};

export const hasClickIdAttribution = ids => !!(ids && (ids.gclid || ids.gbraid || ids.wbraid));
